/* eslint-disable max-lines */
/* eslint-env browser */
import { comparer, computed } from 'mobx';
// @ts-ignore
import * as debounce from 'lodash.debounce';
import { AnalyticsStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ClientLogger } from 'bernie-client';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { merge } from 'bernie-core';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { PackageType } from 'models/multi-item/multi-item-types';

import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkText } from 'uitk-react-text';

import { CarPickupDropOffTimeRange } from 'components/multi-item/search-wizard/car-pickup-dropoff-time-range';
import { EssClientLogger } from 'typeahead-client-logger';
import { fieldMap } from './fields/all-fields';
import { Form } from 'components/common/form';
import { FormFieldBindingComponent } from './fields/form-field-binding';
import { getGridProps } from './grid-item-props';

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');
const { withoutKeys } = merge;
const ButtonTracked = ClickTracker(UitkPrimaryButton);

export interface SearchFormProps {
  analytics?: AnalyticsStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  model?: SearchCriteriaModel;
  autoSubmit?: boolean;
  fetchDatePickerRates?: () => Promise<any> | undefined;
  isMobile?: boolean;
  ignoreFields?: string[];
  displayFields?: string[];
  onUpdateResults?: any;
}

export interface SearchFormState {
  model: SearchCriteriaModel;
  submitDisabled: boolean;
  mounted: boolean;
}

export interface SearchFormFieldProps extends FormFieldBindingComponent {
  model: SearchCriteriaModel;
  modelKey: string;
  validators: any[];
  updateModelCallback?: (event: any) => void;
}

export interface DefaultSubmitButtonProps {
  submitDisabled?: boolean;
  packageType?: PackageType;
  showSubmitButtonIcon?: boolean;
}

const PACAKAGE_DEBOUNCE_TIMEOUT = 2000;
const SA_DEBOUNCE_TIMEOUT = 200;

// NOTE: BFF may provide the keys in the future
export const alwaysIgnoredKeys = ['changedFilters', 'logger', 'comparePropertyIds', 'compareDialog'];

type LocalizedSearchFormProps = LocalizedComponentProps & SearchFormProps;

// @ts-ignore
@withLocalization
@inject('analytics', 'multiItem')
@observer
export class SearchForm extends React.Component<LocalizedSearchFormProps, SearchFormState> {
  constructor(props: LocalizedSearchFormProps) {
    super(props);

    const model = props.model ? props.model.clone() : new SearchCriteriaModel({}, this.props.l10n);

    this.state = {
      model,
      submitDisabled: false,
      mounted: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);

    this.getFormRef = this.getFormRef.bind(this);
    this.dispatchSubmitEvent = debounce(
      this.dispatchSubmitEvent.bind(this),
      this.props.multiItem?.multiItemContext?.packageType && !props.isMobile ? PACAKAGE_DEBOUNCE_TIMEOUT : SA_DEBOUNCE_TIMEOUT,
      { trailing: true }
    );
    this.essAnalyticsLogger = new EssClientLogger(
      props.analytics,
      ClientLogger,
      (model || {}).destination,
      this.props.l10n?.experimentStore?.exposures
    );
  }

  private validators: any[] = [];
  private formRef?: HTMLElement;
  private essAnalyticsLogger: EssClientLogger;

  public static defaultProps = {
    displayFields: [],
    ignoreFields: [],
    provideDefaultSubmitButton: true,
    l10n: {},
  };

  public componentWillReceiveProps(nextProps: { model: SearchCriteriaModel | undefined }) {
    if (nextProps.model !== this.props.model) {
      this.setState({
        model: nextProps.model ? nextProps.model.clone() : new SearchCriteriaModel({}, this.props.l10n),
      });
    }
  }

  public componentDidMount() {
    this.setState({
      mounted: true,
    });
  }

  private get hiddenFields() {
    const { displayFields, ignoreFields } = this.props;
    const { model, mounted } = this.state;
    const excludedKeys: string[] = [];

    if (!mounted) {
      excludedKeys.push('latLong', 'regionId');
    }

    return Object.keys(
      withoutKeys([...alwaysIgnoredKeys, ...(ignoreFields ?? []), ...(displayFields ?? []), ...excludedKeys]).merge({}, model)
    );
  }

  public get modelDefaults() {
    const modelDefaults = SearchCriteriaModel && SearchCriteriaModel.DEFAULTS;

    return modelDefaults || {};
  }

  @computed
  public get displayFieldsActive() {
    const { model } = this.state;
    const { displayFields } = this.props;

    const displayFieldsActive = displayFields?.reduce((activeFields: string[], fieldKey) => {
      // field name override support added to use existing model keys with a new displayField
      const value = model && model[fieldKey as keyof SearchCriteriaModel];
      const defaultValue = this.modelDefaults[fieldKey];

      // if the value and defaultValue are not the same, push fieldKey to activeFields
      if (!comparer.structural(value, defaultValue)) {
        activeFields.push(fieldKey);
      }

      return activeFields;
    }, []);

    return displayFieldsActive;
  }

  @computed
  public get displayFieldsActiveLength() {
    return this.displayFieldsActive && this.displayFieldsActive.length;
  }

  public clearDisplayfields() {
    const model: any = this.state.model;

    // reset all active display fields
    this.displayFieldsActive?.forEach(fieldKey => {
      const defaultValue = this.modelDefaults[fieldKey];

      if (model && fieldKey) {
        model[fieldKey as keyof SearchCriteriaModel] = defaultValue;
      }

      if (fieldKey === 'hotelName') {
        model.selected = '';
      }
    });
  }

  private handleChange(event?: Event) {
    if (document && document.dispatchEvent && typeof document.dispatchEvent === 'function' && CustomEvent) {
      document.dispatchEvent(
        new CustomEvent('mesoSlotFilterUpdate', {
          detail: { type: 'slotFilter', editType: 'adding', filterData: event?.target },
        })
      );
    }

    // enable submit button after model change
    this.setState({ submitDisabled: false });
  }
  private dispatchSubmitEvent(): void {
    const event: Event = new Event('submit', { cancelable: true });
    this.formRef?.dispatchEvent(event);
  }

  private handleSubmit(event?: Event) {
    const { onUpdateResults } = this.props;
    if (this.essAnalyticsLogger) {
      this.essAnalyticsLogger.logWizardSubmitEvent();
    }
    this.setState({ submitDisabled: true });
    const errors: any[] = [];
    this.validators.forEach(validator => {
      const error = validator();
      if (error) {
        errors.push(error);
      }
    });

    if (errors.length > 0) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      const firstFocusableError: any = errors.find((error: any) => error.focus);
      if (firstFocusableError) {
        firstFocusableError.focus();
      }

      this.setState({ submitDisabled: false });

      if (logger && typeof logger.logEvent === 'function') {
        logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_HANDLE_SUBMIT_ERROR'), JSON.stringify(errors));
      }
    } else {
      this.props.analytics?.resetAnalyticsForNewPage();

      if (typeof onUpdateResults === 'function') {
        onUpdateResults(event, this.state.model);
      }
    }
  }

  public render() {
    const { displayFields, multiItem } = this.props;
    const { model, submitDisabled } = this.state;
    const packageType = multiItem?.multiItemContext?.packageType;

    const defaultSubmitButtonProps: DefaultSubmitButtonProps = {
      submitDisabled,
    };

    return (
      <Form
        method="GET"
        action="/Hotel-Search"
        data-stid="sidebar-search-form"
        noValidate={true}
        wrappedFormRef={this.getFormRef}
        onSubmit={this.handleSubmit}
      >
        <Experiment name="HoB_SQM_AA_TEST_1">
          <ExperimentControl />
          <ExperimentVariant bucket={1} />
        </Experiment>
        <Experiment name="HoB_SQM_AA_Test_2">
          <ExperimentControl />
          <ExperimentVariant bucket={1} />
        </Experiment>
        <UitkLayoutGrid {...getGridProps(multiItem)}>
          {displayFields &&
            displayFields.length > 0 &&
            displayFields.map(field => {
              const displayField = (
                <DisplayField
                  key={`displayField-${field}`}
                  field={field}
                  model={model}
                  validators={this.validators}
                  handleChange={this.handleChange}
                />
              );

              return displayField;
            })}
          {this.hiddenFields &&
            this.hiddenFields.length > 0 &&
            this.hiddenFields.map(field => (
              <HiddenField
                key={`hidden_${field}`}
                field={field}
                model={model}
                validators={this.validators}
                handleChange={this.handleChange}
              />
            ))}
          <Viewport>
            <ViewSmall>
              <>
                {packageType === PackageType.HC && (
                  <CarPickupDropOffTimeRange
                    model={this.state.model}
                    modelKey="multiItemCarTimeRange"
                    updateModelCallback={this.handleChange}
                    validators={this.validators}
                  />
                )}
                <DefaultSubmitButton {...defaultSubmitButtonProps} />
              </>
            </ViewSmall>
            <ViewMedium>
              <>
                <DefaultSubmitButton {...defaultSubmitButtonProps} />
                {packageType === PackageType.HC && (
                  <CarPickupDropOffTimeRange
                    model={this.state.model}
                    modelKey="multiItemCarTimeRange"
                    updateModelCallback={this.handleChange}
                    validators={this.validators}
                  />
                )}
              </>
            </ViewMedium>
          </Viewport>
        </UitkLayoutGrid>
      </Form>
    );
  }
  private getFormRef(ref: HTMLElement | undefined) {
    this.formRef = ref;
  }
}

export const DefaultSubmitButton = ({ submitDisabled, packageType, showSubmitButtonIcon }: DefaultSubmitButtonProps) => {
  const submitButtonProps = {
    type: 'submit',
    rfrr: 'FORM.SUBMIT',
    linkName: 'Submit search form',
    isFullWidth: true,
    size: UitkButtonSize.LARGE,
    'data-stid': 'submit-hotel-search',
  };

  const buttonLabel = showSubmitButtonIcon ? <UitkIcon name="search" /> : <LocalizedText message="hotels.search.form.submit.label" />;
  const viewports = [
    <ViewMedium key="medium">
      <UitkLayoutGridItem>
        <div>
          <UitkText align="right">
            <ButtonTracked {...submitButtonProps}>{buttonLabel}</ButtonTracked>
          </UitkText>
        </div>
      </UitkLayoutGridItem>
    </ViewMedium>,
  ];
  viewports.push(
    <ViewSmall key="small">
      <UitkLayoutGridItem>
        <div>
          <ButtonTracked {...submitButtonProps} disabled={submitDisabled}>
            {buttonLabel}
          </ButtonTracked>
        </div>
      </UitkLayoutGridItem>
    </ViewSmall>
  );

  if (packageType === PackageType.FH || packageType === PackageType.FHC) {
    viewports.push(
      <ViewLarge key="large">
        <UitkLayoutGridItem>
          <div>
            <ButtonTracked {...submitButtonProps}>{buttonLabel}</ButtonTracked>
          </div>
        </UitkLayoutGridItem>
      </ViewLarge>
    );
  }

  return <Viewport>{viewports}</Viewport>;
};
DefaultSubmitButton.displayName = 'DefaultSubmitButton';
export const HiddenField = ({ field, model, validators, handleChange }: any) => {
  /*
    Fields that need processing before it can be added to the form to be submitted
    e.g: rooms:Array<RoomModel> --> adults: string, children: string
    This processing should be delegated to the component itself.
   */
  const fieldKeysNeedMutation = ['rooms'];
  if (fieldKeysNeedMutation.includes(field)) {
    return (
      <DisplayField
        key={`displayField-${field}`}
        field={field}
        model={model}
        hidden={true}
        validators={validators}
        handleChange={handleChange}
      />
    );
  }
  const attributes = {
    type: 'hidden',
    name: field,
    value: model[field] || '',
    disabled: false,
  };
  if (attributes.value === '' || (typeof attributes.value.length === 'number' && attributes.value.length === 0)) {
    attributes.disabled = true;
  }

  return <input {...attributes} />;
};
HiddenField.displayName = 'HiddenField';
export const DisplayField = ({
  field,
  model,
  validators,
  handleChange,
  isSubmitted,
  fetchDatePickerRates = undefined,
  essAnalyticsLogger = undefined,
  hidden = false,
}: any) => {
  const SearchFormField: React.ComponentClass<any> = fieldMap[field as keyof typeof fieldMap];
  if (!SearchFormField) {
    return null;
  }

  return (
    <SearchFormField
      model={model}
      modelKey={field}
      validators={validators}
      updateModelCallback={handleChange}
      isSubmitted={isSubmitted}
      fetchDatePickerRates={fetchDatePickerRates}
      essAnalyticsLogger={essAnalyticsLogger}
      hidden={hidden}
    />
  );
};
DisplayField.displayName = 'DisplayField';
